'use client'

import { Mail, MessageCircle } from 'lucide-react'
import Link from 'next/link'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { useUnity } from '@/hooks/use-unity'
import { openSupportChat } from '@/lib/support-chat'

export function ContactSupportModal() {
  const { unity } = useUnity()
  const [isOpen, setIsOpen] = useState(false)

  function handleOpenChat() {
    if (openSupportChat()) {
      setIsOpen(false)
      return
    }

    toast.error(
      'O chat não está disponível no momento. Tente novamente em instantes ou fale conosco por e-mail.'
    )
  }

  return (
    <Dialog onOpenChange={setIsOpen} open={isOpen}>
      <DialogTrigger asChild>
        <div className="leading-none">
          <span className="font-normal text-[#ABACAD] text-sm">
            Enfrentando problemas?
          </span>
          <p className="block cursor-pointer text-primary-500 text-sm underline">
            Falar com nosso suporte.
          </p>
        </div>
      </DialogTrigger>

      <DialogContent className="max-w-sm sm:mx-auto sm:max-w-md md:max-w-2xl lg:max-w-4xl xl:max-w-5xl">
        <DialogHeader className="mt-4 sm:mt-6">
          <DialogTitle className="text-left text-gray-400 text-xs sm:text-sm md:text-base">
            Informamos que a emissão de Notas Fiscais é realizada exclusivamente
            para pagamentos efetuados no ano vigente, em conformidade com a
            política interna da nossa empresa. Não emitimos Notas Fiscais
            retroativas.
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col space-y-4 text-left">
          <h2 className="font-bold text-lg text-primary-900 sm:text-xl md:text-2xl">
            Fale com nossa equipe de suporte
          </h2>
          <p className="text-gray-600 text-sm sm:text-base">
            Estamos à disposição para ajudar você no que precisar.
          </p>
        </div>

        <div className="mt-4 flex flex-col gap-4 sm:mt-6 sm:gap-6 md:flex-row">
          <div className="flex flex-1 flex-col items-center justify-center gap-3 rounded-xl border border-primary-200 bg-primary-50 px-4 py-6 text-center sm:gap-4 sm:px-7 sm:py-10">
            <div className="rounded-1xl bg-primary-500 p-2">
              <Mail className="h-5 w-5 text-primary-50 sm:h-6 sm:w-6" />
            </div>

            <div>
              <h3 className="font-medium text-[#2E2E2E] text-lg sm:text-xl md:text-2.5xl">
                Atendimento Via e-mail
              </h3>
              <span className="font-normal text-[#656566] text-xs sm:text-sm">
                {unity.details?.default_support_email}
              </span>
            </div>

            <Button
              asChild
              className="h-8 max-w-20 cursor-pointer font-semibold text-[#F8F9FA] text-xs sm:h-10 sm:max-w-27.5 sm:text-sm"
              type="button"
            >
              <Link href={`mailto:${unity.details?.default_support_email}`}>
                Enviar Email
              </Link>
            </Button>
          </div>

          <div className="flex flex-1 flex-col items-center justify-center gap-3 rounded-xl border border-[#ABB9FA] bg-[#F5F9FF] px-4 py-6 text-center sm:gap-4 sm:px-7 sm:py-10">
            <div className="rounded-1xl bg-[#1036F2] p-2.5">
              <MessageCircle
                aria-hidden="true"
                className="h-5 w-5 text-[#F8F9FA]"
              />
            </div>

            <div>
              <h3 className="font-medium text-[#2E2E2E] text-lg leading-6 sm:text-xl sm:leading-8 md:text-2.5xl md:leading-10">
                Atendimento Via Chat
              </h3>
              <span className="font-normal text-[#656566] text-xs sm:text-sm">
                Atendimentos em minutos
              </span>
            </div>

            <Button
              className="h-8 max-w-24 cursor-pointer bg-[#1036F2] font-semibold text-[#F8F9FA] text-xs hover:bg-[#0D2BC2] sm:h-10 sm:max-w-33.5 sm:text-sm"
              onClick={handleOpenChat}
              type="button"
            >
              Abrir Chat
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
